// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { pricingModeSchema } from '@/constants/pricing-mode';
import { refineCurrencyAmountString } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { sparseItemSchema, sparseLocationSchema } from '@/sparse/api/types';
import { dateTimeOrNullSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const WHOLE_QUANTITY_PATTERN = /^\d*$/;

// A quantity input value: blank or a non-negative whole number.
export const quantityInputSchema = z
  .string()
  .regex(WHOLE_QUANTITY_PATTERN, 'Quantity must be a whole number');

const servicePeriodSchema = z
  .tuple([dateTimeOrNullSchema, dateTimeOrNullSchema])
  .superRefine(([start, end], ctx) => {
    if (start === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Start date is required',
      });
    }
    if (end === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date is required',
      });
    }
    if (start !== null && end !== null && end < start) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be on or after the start date',
      });
    }
  });

// Form-state shape of an amount input bound to FormCurrencyTextField:
// a bare-string amount carried alongside the charge's locked currency. The
// amount stays a string (an empty amount means "no value"); the currency
// comes from the invoice and is locked in the UI.
const chargeCurrencyAmountSchema = z
  .object({
    amount: z.string(),
    isoCode: z.preprocess(
      (value) => (typeof value === 'string' ? value.toUpperCase() : value),
      isoCurrencyCodeSchema
    ),
  })
  .superRefine(refineCurrencyAmountString);

export const newChargeFormSchema = z
  .object({
    item: sparseItemSchema.nullable(),
    quantity: quantityInputSchema,
    pricingMode: pricingModeSchema,
    unitPrice: chargeCurrencyAmountSchema,
    totalPrice: chargeCurrencyAmountSchema,
    location: sparseLocationSchema.nullable(),
    description: z.string(),
    servicePeriod: servicePeriodSchema,
    expectedBillableDate: dateTimeOrNullSchema,
    taxAmount: chargeCurrencyAmountSchema,
  })
  .refine((value) => value.item !== null, {
    message: 'Item is required',
    path: ['item'],
  })
  .refine((value) => Number(value.quantity) > 0, {
    message: 'Quantity is required',
    path: ['quantity'],
  })
  .refine(
    (value) =>
      value.pricingMode !== 'PER_UNIT' || value.unitPrice.amount !== '',
    {
      message: 'Unit price is required',
      path: ['unitPrice', 'amount'],
    }
  )
  .refine(
    (value) =>
      value.pricingMode !== 'FIXED' || value.totalPrice.amount.length > 0,
    {
      message: 'Amount is required for fixed pricing',
      path: ['totalPrice', 'amount'],
    }
  )
  .refine((value) => value.description.trim().length > 0, {
    message: 'Description is required',
    path: ['description'],
  });

export type NewChargeFormData = z.infer<typeof newChargeFormSchema>;
